import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import {
  EducatorsListingProfileItem,
  EducatorsReliefListingsBooking,
  SharedLearnerReliefListingFacade,
} from "@/types/serializers"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import {
  Empty,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyHeader,
  EmptyContent,
} from "@/components/ui/empty"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Eye, Inbox, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Resources } from "i18next"
import { ListingCard } from "@/components/shared/listing-card"
import { Link, useProps } from "@/lib/page-rendering"

export type IndexProps = {
  bookings: Array<{
    listing_profile: EducatorsListingProfileItem
    collection: Array<
      EducatorsReliefListingsBooking | SharedLearnerReliefListingFacade
    >
  }>
}

const useIndexProps = () => useProps<IndexProps>()

export default function Page() {
  const { t } = useTranslation("educator_relief_listings", {
    keyPrefix: "bookings.index",
  })
  const { bookings } = useIndexProps()

  return (
    <>
      <EducatorPageChrome />
      <PageBody>
        <ul className="flex flex-col gap-6">
          {bookings.length === 0 ? (
            <Card>
              <CardContent>
                <Empty data-testid="bookings-empty">
                  <EmptyHeader>
                    <EmptyMedia
                      variant="icon"
                      className="text-muted size-20 bg-white"
                    >
                      <Inbox className="size-16" />
                    </EmptyMedia>
                    <EmptyTitle>{t("empty_list_title")}</EmptyTitle>
                    <EmptyDescription>
                      {t("empty_list_description")}
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button asChild>
                      <Link href={"/search/relief_listings"}>
                        {t("search")}
                      </Link>
                    </Button>
                  </EmptyContent>
                </Empty>
              </CardContent>
            </Card>
          ) : (
            bookings.map(({ listing_profile, collection }) => (
              <li key={listing_profile.id} className="contents">
                <ListingCard listing={listing_profile}>
                  {collection.map((booking) => (
                    <BookingCollectionCard booking={booking} key={booking.id} />
                  ))}
                </ListingCard>
              </li>
            ))
          )}
        </ul>
      </PageBody>
    </>
  )
}

function BookingCollectionCard({
  booking,
}: {
  booking: EducatorsReliefListingsBooking | SharedLearnerReliefListingFacade
}) {
  const { t } = useTranslation("educator_relief_listings", {
    keyPrefix: "bookings.index",
  })
  const { t: tBookingType } = useTranslation("educator_relief_listings", {
    keyPrefix: "bookings.types",
  })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })

  return (
    <Card role="listitem" key={booking.id} data-testid="relief-booking-card">
      <CardHeader>
        <CardTitle>{booking.title}</CardTitle>
        <Badge data-testid="listing-type">
          {tBookingType(booking.listing_type)}
        </Badge>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-base">
          <dl className="grid w-fit grid-cols-2 gap-2 [&_dd]:font-medium">
            {booking.listing_type === "learner" ? (
              <>
                <dt>{t("subject")}</dt>
                <dd data-testid="subject">
                  {tSubjects(
                    booking.subject_key as keyof Resources["subjects"]["subjects"],
                  )}
                </dd>
              </>
            ) : null}
            <dt>{t("start_date")}</dt>
            <dd data-testid="start_date">{booking.start_date}</dd>
            <dt>{t("end_date")}</dt>
            <dd data-testid="end_date">{booking.end_date}</dd>
          </dl>
        </CardDescription>
      </CardContent>
      <CardFooter className="flex flex-row flex-wrap gap-4">
        <Button asChild>
          <Link href={`/search/relief_listings/${booking.id}`}>
            <Eye />
            {t("actions.view_listing")}
          </Link>
        </Button>
        {booking.listing_type === "educator" && (
          <Button asChild>
            <Link href={`./bookings/${booking.id}/students`}>
              <Users />
              {t("actions.view_students")}
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}
